import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StandardTable } from '../components/common/UI/StandardTable';
import { useAccount } from '../contexts/AccountContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { allocateTeamLimits, getClientData } from '../services/adminService';
import { allocateUserLimits } from '../services/api';
import { getBillingLedger } from '../services/billingService';
import { getTeam, getTeamsByClient, updateTeam } from '../services/teamService';
import { getUsersByTeam, updateUserRole } from '../services/userService';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { User } from '../types/auth';
import type { UserLimits } from '../types/user';

const EMPTY_OBJECT: Record<string, never> = {};

interface TeamLimits {
  numTenders?: number;
  numDrafts?: number;
  numReviews?: number;
  numReports?: number;
  numAdHocDocs?: number;
  numSectionChats?: number;
}

interface Team {
  id: string;
  teamName: string;
  description?: string;
  teamLeadId?: string;
  limits?: TeamLimits;
  clientId: string;
}

const TeamDetailsPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { account, hasPermission } = useAccount();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);

  // Edit State
  const [editingTeamName, setEditingTeamName] = useState('');
  const [editingTeamDescription, setEditingTeamDescription] = useState('');
  const [selectedLead, setSelectedLead] = useState<string>('');
  const [limits, setLimits] = useState<TeamLimits>({});
  const [memberLimits, setMemberLimits] = useState<Record<string, UserLimits>>({});
  const [saving, setSaving] = useState(false);

  // Client Limits State
  const [clientLimits, setClientLimits] = useState<TeamLimits>({});
  const [otherTeamsUsage, setOtherTeamsUsage] = useState<TeamLimits>({});
  const [isClientPayg, setIsClientPayg] = useState(false);

  // Permissions
  const canUpdateTeamDetails = hasPermission(PERMISSIONS.TEAM_UPDATE);
  const canManageTeamLimits =
    hasPermission(PERMISSIONS.TEAM_MANAGE_LIMITS) || hasPermission(PERMISSIONS.SYSTEM_MANAGE);
  const canManageUserLimits =
    hasPermission(PERMISSIONS.USER_MANAGE_LIMITS) || hasPermission(PERMISSIONS.SYSTEM_MANAGE);

  const isTeamLead = account?.role === 'team_lead' && account?.teamId === teamId;

  const fetchTeamData = useCallback(async () => {
    if (!teamId) {
      return;
    }
    setLoading(true);
    try {
      const teamData = await getTeam(teamId);
      if (teamData) {
        setTeam(teamData);
        setEditingTeamName(teamData.teamName);
        setEditingTeamDescription(teamData.description || '');
        setSelectedLead(teamData.teamLeadId || '');
        setLimits(teamData.limits || EMPTY_OBJECT);

        // Fetch Client Limits & Other Teams Usage (If Admin)
        if (canManageTeamLimits && teamData.clientId) {
          try {
            // 1. Fetch Client
            const cData = await getClientData(teamData.clientId);
            let isPayg = false;
            if (cData) {
              // Prioritize subscription limits, fallback to root limits
              setClientLimits(
                cData.subscription?.limits ||
                  (cData as unknown as Record<string, unknown>).limits ||
                  EMPTY_OBJECT
              );
              isPayg = cData.subscription?.billing_model === 'payg';
            }

            // 1b. Fetch Ledger
            const ledgerData = await getBillingLedger(teamData.clientId);
            if (ledgerData) {
              const ledgerPayg = ledgerData.isPAYG;
              if (ledgerPayg !== undefined) {
                isPayg = ledgerPayg;
              }
            }
            setIsClientPayg(isPayg);

            // 2. Fetch Other Teams
            const allTeams = await getTeamsByClient(teamData.clientId);
            const usage: TeamLimits = {
              numTenders: 0,
              numDrafts: 0,
              numReviews: 0,
              numReports: 0,
              numAdHocDocs: 0,
              numSectionChats: 0,
            };

            for (const tData of allTeams) {
              if (tData.id !== teamId) {
                const l = tData.limits || EMPTY_OBJECT;
                usage.numTenders = (usage.numTenders || 0) + (l.numTenders || 0);
                usage.numDrafts = (usage.numDrafts || 0) + (l.numDrafts || 0);
                usage.numReviews = (usage.numReviews || 0) + (l.numReviews || 0);
                usage.numReports = (usage.numReports || 0) + (l.numReports || 0);
                usage.numAdHocDocs = (usage.numAdHocDocs || 0) + (l.numAdHocDocs || 0);
                usage.numSectionChats = (usage.numSectionChats || 0) + (l.numSectionChats || 0);
              }
            }
            setOtherTeamsUsage(usage);
          } catch (_e) {
            // console.error('Failed to fetch client limits context', e);
          }
        }

        // Fetch Users
        if (hasPermission(PERMISSIONS.USER_VIEW) || isTeamLead) {
          const teamUsers = await getUsersByTeam(teamId, teamData.clientId);
          setUsers(teamUsers);

          // Initialize member limits
          const initialMemberLimits: Record<string, UserLimits> = {};
          for (const u of teamUsers) {
            initialMemberLimits[u.uid] = {
              numTenders: u.limits?.numTenders || 0,
              numDrafts: u.limits?.numDrafts || 0,
              numReviews: u.limits?.numReviews || 0,
              numReports: u.limits?.numReports || 0,
              numAdHocDocs: u.limits?.numAdHocDocs || 0,
              numSectionChats: u.limits?.numSectionChats || 0,
            };
          }
          setMemberLimits(initialMemberLimits);
        }
      } else {
        showSnackbar('Team not found', 'error');
        navigate(-1);
      }
    } catch (_err) {
      showSnackbar('Failed to load team data', 'error');
    } finally {
      setLoading(false);
    }
  }, [teamId, hasPermission, isTeamLead, navigate, showSnackbar, canManageTeamLimits]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const handleLimitChange = (key: keyof TeamLimits, val: string) => {
    let numVal = parseInt(val, 10);
    if (Number.isNaN(numVal)) {
      numVal = 0;
    }
    numVal = Math.max(-1, numVal);
    setLimits((prev) => ({ ...prev, [key]: numVal }));
  };

  const handleMemberLimitChange = (uid: string, key: keyof UserLimits, val: string) => {
    let numVal = parseInt(val, 10);
    if (Number.isNaN(numVal)) {
      numVal = 0;
    }
    numVal = Math.max(-1, numVal);
    setMemberLimits((prev) => {
      const current = prev[uid] || {
        numTenders: 0,
        numDrafts: 0,
        numReviews: 0,
        numReports: 0,
        numAdHocDocs: 0,
        numSectionChats: 0,
      };
      return {
        ...prev,
        [uid]: {
          ...current,
          [key]: numVal,
        },
      };
    });
  };

  // Stats for Member Allocation (Team Limit vs Member Sum)
  const getColumnStats = (key: keyof TeamLimits) => {
    let teamLimit = limits[key] || 0;
    if (teamLimit === Infinity) {
      teamLimit = -1;
    }
    let allocated = 0;
    for (const l of Object.values(memberLimits)) {
      const v = l[key as keyof UserLimits] || 0;
      if (v !== -1 && v !== Infinity) {
        allocated += v;
      }
    }
    const available = teamLimit === -1 ? Infinity : teamLimit - allocated;
    return { teamLimit, allocated, available };
  };

  // Stats for Team Allocation (Client Limit vs Team Sum)
  const getClientStats = (key: keyof TeamLimits) => {
    let max = isClientPayg ? -1 : clientLimits[key] || 0;
    if (max === Infinity) {
      max = -1;
    }
    const usedByOthers = otherTeamsUsage[key] || 0;
    const availableForTeam = max === -1 ? Infinity : Math.max(0, max - usedByOthers);
    return { max, usedByOthers, availableForTeam };
  };

  const LIMIT_KEYS: Array<keyof UserLimits> = [
    'numTenders',
    'numDrafts',
    'numReviews',
    'numReports',
    'numAdHocDocs',
    'numSectionChats',
  ];

  const LABELS: Record<string, string> = {
    numTenders: 'Tenders',
    numDrafts: 'Drafts',
    numReviews: 'Reviews',
    numReports: 'Reports',
    numAdHocDocs: 'Ad-hoc',
    numSectionChats: 'AI Messages',
  };

  const hasAllocationErrors = LIMIT_KEYS.some((key) => {
    const { available } = getColumnStats(key as keyof TeamLimits);
    return available < 0;
  });

  const handleSave = async () => {
    if (!team || !teamId) {
      return;
    }

    if (editingTeamName.length > 50) {
      showSnackbar('Team name must be 50 characters or less', 'error');
      return;
    }

    if (editingTeamDescription.length > 255) {
      showSnackbar('Team description must be 255 characters or less', 'error');
      return;
    }

    setSaving(true);
    try {
      // 1. Update Team Limits
      if (canManageTeamLimits) {
        await allocateTeamLimits(teamId, limits as Record<string, number>);
      }

      // 2. Update Member Limits
      const updatePromises = Object.entries(memberLimits).map(([uid, uLimits]) =>
        allocateUserLimits(uid, uLimits)
      );
      await Promise.all(updatePromises);

      // 3. Update Team Meta (Name, Description, Lead)
      const updates: Partial<Team> = {};
      if (editingTeamName.trim() && editingTeamName !== team.teamName) {
        updates.teamName = editingTeamName.trim();
      }
      if (editingTeamDescription.trim() !== (team.description || '')) {
        updates.description = editingTeamDescription.trim();
      }

      if (Object.keys(updates).length > 0) {
        await updateTeam(teamId, updates);
      }

      if (selectedLead !== team.teamLeadId) {
        await updateTeam(teamId, {
          teamLeadId: selectedLead || null,
        } as Record<string, unknown>);
        if (selectedLead) {
          const user = users.find((u) => u.uid === selectedLead);
          if (user && user.role === 'user') {
            await updateUserRole(user.uid, 'team_lead');
          }
        }
      }

      showSnackbar('Team updated successfully', 'success');
      setTeam((prev) =>
        prev
          ? {
              ...prev,
              teamName: editingTeamName.trim(),
              description: editingTeamDescription.trim(),
              teamLeadId: selectedLead,
              limits,
            }
          : null
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      showSnackbar(`Failed to update: ${message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer maxWidth={false}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (!team) {
    return null;
  }

  return (
    <PageContainer title={`Team: ${team.teamName}`} maxWidth={false}>
      <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {team.teamName}
              </Typography>
            </Box>
          </Box>
          {(canUpdateTeamDetails || canManageTeamLimits || canManageUserLimits) && (
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={
                  saving ||
                  hasAllocationErrors ||
                  editingTeamName.length > 50 ||
                  editingTeamDescription.length > 255
                }
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </Box>

        {/* Settings & Limits - Vertical Column Layout */}
        <Paper sx={{ mb: 4, p: 3 }}>
          <Grid container spacing={4}>
            {/* Column 1: Team Details */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="h6" gutterBottom>
                Team Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Team Name"
                  value={editingTeamName}
                  onChange={(e) => setEditingTeamName(e.target.value)}
                  fullWidth
                  disabled={!canUpdateTeamDetails}
                  inputProps={{ maxLength: 50 }}
                  helperText={`${editingTeamName.length}/50`}
                  error={editingTeamName.length > 50}
                />
                <TextField
                  label="Description (Optional)"
                  value={editingTeamDescription}
                  onChange={(e) => setEditingTeamDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={!canUpdateTeamDetails}
                  inputProps={{ maxLength: 255 }}
                  helperText={`${editingTeamDescription.length}/255`}
                  error={editingTeamDescription.length > 255}
                />
                <FormControl fullWidth disabled={!canUpdateTeamDetails}>
                  <InputLabel id="team-lead-label">Team Lead</InputLabel>
                  <Select
                    labelId="team-lead-label"
                    value={selectedLead}
                    label="Team Lead"
                    onChange={(e) => setSelectedLead(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.uid} value={user.uid}>
                        {user.displayName || user.email} ({user.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Members
                  </Typography>
                  <Typography variant="h4">{users.length}</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Column 2: Team Limits */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="h6" gutterBottom>
                Team Limits
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {LIMIT_KEYS.map((key) => {
                  const { availableForTeam, max } = getClientStats(key as keyof TeamLimits);
                  return (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ width: '120px' }}>
                        {LABELS[key]}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <TextField
                          type="number"
                          size="small"
                          label="Allocated"
                          value={limits[key] === -1 ? '' : limits[key] || 0}
                          onChange={(e) => {
                            handleLimitChange(key as keyof TeamLimits, e.target.value);
                          }}
                          disabled={!canManageTeamLimits || limits[key] === -1}
                          sx={{ width: '100px' }}
                          inputProps={{ min: 0 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        {max === -1 && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={limits[key] === -1}
                                onChange={(e) => {
                                  handleLimitChange(
                                    key as keyof TeamLimits,
                                    e.target.checked ? '-1' : '0'
                                  );
                                }}
                                disabled={!canManageTeamLimits}
                              />
                            }
                            label="Unlimited"
                          />
                        )}
                        {canManageTeamLimits && !isClientPayg && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ flex: 1, textAlign: 'right' }}
                          >
                            Available:{' '}
                            <strong>
                              {availableForTeam === Infinity ? '∞' : availableForTeam}
                            </strong>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Member Allocations Table */}
        <Paper sx={{ mb: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Member Allocations
          </Typography>
          <Box
            sx={{
              '& th': {
                backgroundColor: theme.palette.greyContrast,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            }}
          >
            <StandardTable
              columns={[
                {
                  id: 'member',
                  label: 'Member',
                  width: 150,
                  render: (member) => (
                    <>
                      <Typography variant="body2" fontWeight="medium">
                        {member.displayName || member.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {member.role}
                      </Typography>
                    </>
                  ),
                },
                ...LIMIT_KEYS.map((key) => {
                  const { available, teamLimit } = getColumnStats(key as keyof TeamLimits);
                  const isOver = available < 0;
                  return {
                    id: key,
                    label: (
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ fontSize: '0.9rem', color: 'inherit' }}
                        >
                          {LABELS[key]}
                        </Typography>
                        <Chip
                          label={`Avail: ${available === Infinity ? '∞' : available} / ${teamLimit === -1 ? '∞' : teamLimit}`}
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontWeight: 'bold',
                            backgroundColor: isOver
                              ? theme.palette.error.main
                              : 'rgba(255, 255, 255, 0.15)',
                            color: isOver ? theme.palette.error.contrastText : 'inherit',
                          }}
                        />
                      </Box>
                    ),
                    align: 'center' as const,
                    width: 100,
                    render: (member: User) => {
                      const userLims = memberLimits[member.uid] || {
                        numTenders: 0,
                        numDrafts: 0,
                        numReviews: 0,
                        numReports: 0,
                        numAdHocDocs: 0,
                        numSectionChats: 0,
                      };
                      const val = userLims[key] || 0;
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <TextField
                            type="number"
                            size="small"
                            value={val === -1 ? '' : val}
                            onChange={(e) => {
                              handleMemberLimitChange(member.uid, key, e.target.value);
                            }}
                            disabled={!canManageUserLimits || val === -1}
                            inputProps={{
                              min: 0,
                              style: { textAlign: 'center', padding: '4px' },
                            }}
                            sx={{
                              width: '80px',
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: isOver ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                                },
                              },
                            }}
                          />
                          {teamLimit === -1 && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={val === -1}
                                  onChange={(e) => {
                                    handleMemberLimitChange(
                                      member.uid,
                                      key,
                                      e.target.checked ? '-1' : '0'
                                    );
                                  }}
                                  disabled={!canManageUserLimits}
                                  sx={{ p: 0.5 }}
                                />
                              }
                              label={<Typography variant="caption">Unlimited</Typography>}
                              sx={{ m: 0 }}
                            />
                          )}
                        </Box>
                      );
                    },
                  };
                }),
              ]}
              data={users}
              keyExtractor={(member) => member.uid}
              emptyStateMessage="No members found"
              size="small"
              stickyHeader
              rowSx={(_member) => ({
                '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
                '& td': {
                  ...LIMIT_KEYS.reduce<Record<string, { backgroundColor: string }>>((acc, key) => {
                    const { available } = getColumnStats(key as keyof TeamLimits);
                    if (available < 0) {
                      acc[`&:nth-of-type(${LIMIT_KEYS.indexOf(key) + 2})`] = {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      };
                    }
                    return acc;
                  }, {}),
                },
              })}
            />
          </Box>
        </Paper>
      </Box>
    </PageContainer>
  );
};

export default TeamDetailsPage;
